import React, { useState } from "react";
import styled from "styled-components";
import { Card, Badge, Collapse, Spinner } from "react-bootstrap";
import {
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  Activity,
  AlertTriangle,
  AlertOctagon,
  Image as ImageIcon,
} from "react-feather";
import { formatDistance } from "date-fns";
import {
  ActivityLogLevel,
  ActivityType,
  ITaskActivitySummary,
  IActivityLog,
  IAssetResponse,
} from "../../../interfaces";
import { LogItem } from "./LogItem";
import { ScreenshotModal } from "./screenshot-modal";
import { ScreenshotThumbnail } from "./screenshot-thumbnail";
import { useApi } from "../../../hooks";

interface Props {
  activity: ITaskActivitySummary;
  taskId: string;
  minLogLevel: ActivityLogLevel;
}

const ActivityCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const ActivityHeader = styled(Card.Header)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  background-color: #f8f9fa;
  border-bottom: ${(props) => (props.expanded ? "1px solid #dee2e6" : "none")};
`;

const ActivityTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ActivityInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ActivityIcon = styled(Activity)`
  margin-right: 5px;
`;

const CostBadge = styled.div`
  display: flex;
  align-items: center;
  background-color: #e9f5ff;
  color: #0366d6;
  padding: 4px 8px;
  border-radius: 16px;
  font-size: 0.85rem;
`;

const CostIcon = styled(DollarSign)`
  width: 14px;
  height: 14px;
  margin-right: 4px;
`;

const TimeInfo = styled.div`
  display: flex;
  align-items: center;
  color: #666;
  font-size: 0.85rem;
`;

const TimeIcon = styled(Clock)`
  width: 14px;
  height: 14px;
  margin-right: 4px;
`;

const AlertBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const WarningBadge = styled(Badge)`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 5px;
`;

const ErrorBadge = styled(Badge)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const WarningIcon = styled(AlertTriangle)`
  width: 12px;
  height: 12px;
`;

const ErrorIcon = styled(AlertOctagon)`
  width: 12px;
  height: 12px;
`;

const LogsContainer = styled(Card.Body)`
  padding: 16px;
`;

const NoLogs = styled.div`
  padding: 12px;
  text-align: center;
  color: #666;
  font-style: italic;
`;

const MetadataSection = styled.div`
  margin-top: 16px;
  border-top: 1px solid #dee2e6;
  padding-top: 16px;
`;

const ScreenshotsSection = styled.div`
  margin-top: 16px;
  border-top: 1px solid #dee2e6;
  padding-top: 16px;
`;

const ScreenshotCarousel = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding: 8px 0;
`;

const ModalContent = styled.div`
  display: flex;
  height: 70vh;
`;

const ImageContainer = styled.div`
  flex: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
`;

const FullImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const MetadataContainer = styled.div`
  flex: 1;
  padding: 20px;
  background-color: #f8f9fa;
  overflow-y: auto;
`;

const MetadataTitle = styled.h6`
  margin-bottom: 16px;
  color: #495057;
`;

const MetadataJson = styled.pre`
  background-color: #fff;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 12px;
  font-size: 12px;
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const ScreenshotIndicator = styled.div`
  display: flex;
  align-items: center;
  background-color: #e7f3ff;
  color: #0366d6;
  padding: 4px 8px;
  border-radius: 16px;
  font-size: 0.75rem;
  gap: 4px;
`;

const ScreenshotCountIcon = styled(ImageIcon)`
  width: 12px;
  height: 12px;
`;

const getActivityTypeDisplay = (type: ActivityType): string => {
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getActivityDuration = (activity: ITaskActivitySummary): string | null => {
  if (!activity.metadata?.startTime || !activity.metadata?.endTime) {
    return null;
  }

  const startTime = new Date(activity.metadata.startTime);
  const endTime = new Date(activity.metadata.endTime);

  return formatDistance(startTime, endTime, { includeSeconds: true });
};

const isLogLevelVisible = (
  logLevel: ActivityLogLevel,
  minLogLevel: ActivityLogLevel
): boolean => {
  const levels = [
    ActivityLogLevel.DEBUG,
    ActivityLogLevel.INFO,
    ActivityLogLevel.WARN,
    ActivityLogLevel.ERROR,
  ];

  return levels.indexOf(logLevel) >= levels.indexOf(minLogLevel);
};

export const ActivityItem: React.FC<Props> = ({
  activity,
  minLogLevel,
  taskId,
}) => {
  const { get } = useApi();
  const [expanded, setExpanded] = useState(false);
  const [selectedScreenshot, setSelectedScreenshot] =
    useState<IAssetResponse | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [logs, setLogs] = useState<IActivityLog[] | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);

  const { llmCost, type, metadata, warningCount, errorCount } = activity;

  const visibleLogs = (logs ?? []).filter((log) =>
    isLogLevelVisible(log.level, minLogLevel)
  );
  const duration = getActivityDuration(activity);
  const screenshotCount = activity.screenshots?.length || 0;

  const handleToggle = async () => {
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);
    // Fetch logs the first time the activity is expanded
    if (nextExpanded && logs === null && !logsLoading) {
      setLogsLoading(true);
      try {
        const fetched = await get<IActivityLog[]>(
          `/tasks/${taskId}/activities/${activity._id}/logs`
        );
        setLogs(fetched ?? []);
      } catch (err) {
        console.error("Failed to fetch activity logs:", err);
        setLogs([]);
      } finally {
        setLogsLoading(false);
      }
    }
  };

  const handleScreenshotClick = (screenshot: IAssetResponse) => {
    setSelectedScreenshot(screenshot);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedScreenshot(null);
  };

  return (
    <>
      <ActivityCard>
        <ActivityHeader expanded={expanded} onClick={handleToggle}>
          <ActivityTitle>
            <ActivityIcon />
            <span>{getActivityTypeDisplay(type)}</span>
            {expanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </ActivityTitle>

          <ActivityInfo>
            {(warningCount > 0 || errorCount > 0) && (
              <AlertBadge>
                {warningCount > 0 && (
                  <WarningBadge bg="warning" text="dark">
                    <WarningIcon />
                    {warningCount}
                  </WarningBadge>
                )}
                {errorCount > 0 && (
                  <ErrorBadge bg="danger">
                    <ErrorIcon />
                    {errorCount}
                  </ErrorBadge>
                )}
              </AlertBadge>
            )}

            {!expanded && screenshotCount > 0 && (
              <ScreenshotIndicator>
                <ScreenshotCountIcon />
                <span>{screenshotCount}</span>
              </ScreenshotIndicator>
            )}

            {duration && (
              <TimeInfo>
                <TimeIcon />
                <span>{duration}</span>
              </TimeInfo>
            )}

            {llmCost !== undefined && (
              <CostBadge>
                <CostIcon />
                <span>${llmCost.toFixed(5)}</span>
              </CostBadge>
            )}
          </ActivityInfo>
        </ActivityHeader>

        <Collapse in={expanded}>
          <div>
            <LogsContainer>
              {logsLoading ? (
                <div className="d-flex justify-content-center py-3">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : visibleLogs.length > 0 ? (
                visibleLogs.map((log, index) => (
                  <LogItem key={`log-${activity._id}-${index}`} {...log} />
                ))
              ) : (
                <NoLogs>No logs match the current filter level</NoLogs>
              )}

              {expanded &&
                activity.screenshots &&
                activity.screenshots.length > 0 && (
                  <ScreenshotsSection>
                    <h6>Screenshots ({activity.screenshots.length})</h6>
                    <ScreenshotCarousel>
                      {activity.screenshots.map((screenshot, index) => (
                        <ScreenshotThumbnail
                          key={screenshot._id}
                          taskId={taskId}
                          activityId={activity._id}
                          screenshot={screenshot}
                          index={index}
                          onClick={() => handleScreenshotClick(screenshot)}
                        />
                      ))}
                    </ScreenshotCarousel>
                  </ScreenshotsSection>
                )}

              {expanded && metadata && Object.keys(metadata).length > 0 && (
                <MetadataSection>
                  <h6>Activity Details</h6>
                  <pre>{JSON.stringify(metadata, null, 2)}</pre>
                </MetadataSection>
              )}
            </LogsContainer>
          </div>
        </Collapse>
      </ActivityCard>

      <ScreenshotModal
        taskId={taskId}
        activityId={activity._id}
        screenshot={selectedScreenshot}
        show={showModal}
        onClose={handleCloseModal}
      />
    </>
  );
};
